/**
 * Rebel Tide Hoodies — <rt-intro>, the "001 OF 300" opening.
 * A plain script (not a module) that its section loads with `async` right
 * after the element: the count starts as soon as the page shows, without
 * waiting for the rest of the page to load. Nothing else depends on it.
 */
(() => {
  if (customElements.get('rt-intro')) return;

  const cfg = (window.RT && window.RT.config) || {};
  const html = document.documentElement;
  const motionLevel = () => html.getAttribute('data-rt-motion') || 'full';

  /* ------------------------------------------------------------------ *
   * <rt-intro> — black screen, thin numbers roll 001 → 300, then the photo
   * opens and the headline comes in: by itself right after the count
   * (data-open="auto") or as the visitor scrolls (data-open="scroll").
   * Opening by itself, a tap, click, wheel, swipe or key during the count
   * skips straight to the open photo.
   * ------------------------------------------------------------------ */

  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const SKIP_EVENTS = ['wheel', 'touchmove', 'keydown'];
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class RtIntro extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.stage = this.querySelector('.rt-intro__stage');
      this.copyEl = this.querySelector('.rt-intro__copy');
      this.digitsEl = this.querySelector('[data-digits]');
      this.from = Number(this.dataset.from) || 1;
      this.to = Number(this.dataset.to) || Number(cfg.editionSize) || 300;
      this.pad = Number(this.dataset.pad) || String(this.to).length;
      this.duration = Number(this.dataset.duration) || 1200;
      this.openMs = Number(this.dataset.openMs) || 900;
      this.mode = this.dataset.open === 'scroll' ? 'scroll' : 'auto';
      this.progress = 0;

      const level = motionLevel();
      const seen = html.getAttribute('data-rt-intro') === 'done';
      if (level === 'off' || seen) {
        this.setStatic();
        return;
      }

      this.lite = level === 'lite';
      if (this.mode === 'auto') {
        this.classList.add('is-auto');
        this.bindSkip();
      }
      this.buildDigits();

      // Wait for the thin font so the digits don't jump mid-roll (max 400ms).
      const fontsReady = document.fonts?.ready ?? Promise.resolve();
      Promise.race([fontsReady, wait(400)]).then(() => {
        if (this.isConnected && !this.skipped) this.count();
      });

      if (this.mode === 'scroll') this.bindScroll();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.openRaf);
      clearTimeout(this.openTimer);
      this.unbindSkip();
      if (this.onScroll) {
        document.removeEventListener('scroll', this.onScroll, { capture: true });
        window.removeEventListener('resize', this.onScroll);
      }
      this.io?.disconnect();
    }

    /** Opening by itself: any tap, click, wheel, swipe or key skips to the open photo. */
    bindSkip() {
      this.onSkip = (event) => {
        if (event.type === 'keydown' && (event.metaKey || event.ctrlKey || event.altKey)) return;
        if (event.type === 'pointerdown' && event.button > 0) return;
        this.skip();
      };
      this.addEventListener('pointerdown', this.onSkip);
      SKIP_EVENTS.forEach((type) => window.addEventListener(type, this.onSkip, { passive: true }));
    }

    unbindSkip() {
      if (!this.onSkip) return;
      this.removeEventListener('pointerdown', this.onSkip);
      SKIP_EVENTS.forEach((type) => window.removeEventListener(type, this.onSkip));
      this.onSkip = null;
    }

    skip() {
      if (this.skipped || this.classList.contains('is-open')) return;
      this.skipped = true;
      this.unbindSkip();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.openTimer);
      if (!this.classList.contains('is-counted')) this.counted(true);
      this.autoOpen(this.lite ? 200 : 380);
    }

    setStatic() {
      this.classList.add('is-static', 'is-counted');
      this.render(this.to);
      this.copyEl?.setAttribute('data-active', '');
    }

    buildDigits() {
      if (!this.digitsEl) return;
      if (this.lite) {
        this.render(this.from);
        return;
      }
      this.digitsEl.textContent = '';
      this.strips = [];
      for (let k = this.pad - 1; k >= 0; k--) {
        const col = document.createElement('span');
        col.className = 'rt-intro__col';
        const strip = document.createElement('span');
        strip.className = 'rt-intro__strip';
        for (let d = 0; d <= 10; d++) {
          const cell = document.createElement('span');
          cell.textContent = String(d % 10);
          strip.appendChild(cell);
        }
        col.appendChild(strip);
        this.digitsEl.appendChild(col);
        this.strips.push({ k, strip });
      }
      this.render(this.from);
    }

    /** Odometer: each column rolls, higher columns carry smoothly. @param {number} value */
    render(value) {
      if (!this.digitsEl) return;
      if (!this.strips) {
        this.digitsEl.textContent = String(Math.round(value)).padStart(this.pad, '0');
        return;
      }
      for (const { k, strip } of this.strips) {
        const place = Math.pow(10, k);
        let pos;
        if (k === 0) {
          pos = value % 10;
        } else {
          const lower = value % place;
          const carry = Math.max(0, lower - (place - 1));
          pos = (Math.floor(value / place) + carry) % 10;
        }
        strip.style.transform = `translate3d(0, ${(-pos).toFixed(3)}em, 0)`;
      }
    }

    count() {
      const start = performance.now();
      const { from, to, duration } = this;
      const tick = (now) => {
        const t = clamp01((now - start) / duration);
        const value = from + (to - from) * easeInOutCubic(t);
        this.render(this.lite ? Math.round(value) : value);
        if (t < 1) {
          this.raf = requestAnimationFrame(tick);
        } else {
          this.render(to);
          this.counted();
        }
      };
      this.raf = requestAnimationFrame(tick);
    }

    /** @param {boolean} [skipped] - jumped to the end: the caller opens the photo */
    counted(skipped = false) {
      // Final frame as plain text: crisp and easy to read.
      if (this.digitsEl) {
        this.strips = null;
        this.digitsEl.textContent = String(this.to).padStart(this.pad, '0');
      }
      this.classList.add('is-counted');
      this.style.setProperty('--hint', '1');
      if (this.mode === 'auto' && !skipped) {
        this.openTimer = setTimeout(() => this.autoOpen(), this.lite ? 0 : 160);
      }
    }

    /** Opens the photo from where it is now. @param {number} [duration] */
    autoOpen(duration = this.lite ? Math.min(this.openMs, 600) : this.openMs) {
      cancelAnimationFrame(this.openRaf);
      const from = this.progress || 0;
      const start = performance.now();
      const step = (now) => {
        const t = clamp01((now - start) / duration);
        this.apply(from + (1 - from) * t);
        if (t < 1) this.openRaf = requestAnimationFrame(step);
        else this.opened();
      };
      this.openRaf = requestAnimationFrame(step);
    }

    opened() {
      this.unbindSkip();
      this.classList.add('is-open');
      this.markSeen();
    }

    bindScroll() {
      this.visible = true;
      this.onScroll = () => {
        if (this.ticking || !this.visible) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      // Capture catches scrolling of Horizon's .page-wrapper on desktop and of the window on mobile.
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          if (this.visible) this.update();
        });
        this.io.observe(this);
      }
      this.update();
    }

    update() {
      const rect = this.getBoundingClientRect();
      const viewport = this.stage?.clientHeight || window.innerHeight;
      const travel = rect.height - viewport;
      const progress = travel > 0 ? clamp01(-rect.top / travel) : 1;
      this.apply(progress);
    }

    /** @param {number} p progress 0 → 1 */
    apply(p) {
      this.progress = p;
      const open = easeInOutCubic(clamp01((p - 0.02) / 0.5));
      const fade = clamp01((p - 0.02) / 0.26);
      const copy = clamp01((p - 0.42) / 0.38);
      this.style.setProperty('--open', open.toFixed(4));
      this.style.setProperty('--fade', fade.toFixed(4));
      this.style.setProperty('--copy', copy.toFixed(4));
      if (copy > 0.6) this.copyEl?.setAttribute('data-active', '');
      else this.copyEl?.removeAttribute('data-active');
      if (p > 0.9) this.markSeen();
    }

    markSeen() {
      if (this.seen) return;
      this.seen = true;
      try {
        sessionStorage.setItem('rt-intro-seen', '1');
      } catch (e) {
        /* private mode */
      }
    }
  }

  customElements.define('rt-intro', RtIntro);
})();